// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import * as React from "react";

import { css } from "styled-components";

import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import useReportApi from "@store/Framework/Common/Hooks/useReportAPI/useReportAPI";
import useClear from "@store/Framework/Store/Bunzl/Pages/Reporting/StoreHooks/useClear";
import { CustomWidgetModule } from "@store/Framework/Types/types";
import AccordionLayout from "@store/Library/Components/Reporting/Common/AccordionLayout";
import ApiButtons from "@store/Library/Components/Reporting/Common/ApiButtons";
import ReportContainer from "@store/Library/Components/Reporting/Common/ReportContainer";
import { LocationPicker } from "@store/Library/Components/Reporting/LocationPicker/LocationPicker";

import Checkbox from "@insite/mobius/Checkbox";
import { isMonthlyEpensiveComparisonModel } from "@store/Framework/Common/Hooks/useReportAPI/ReportApiReportHandlers";
import { MonthlyExpenseComparisonItemModel } from "@store/Framework/Services/Reporting/ReportingModels";
import { CompanyLocation } from "@store/Framework/Store/Bunzl/Pages/Reporting/LoadLocations";
import {
    locationsSelector,
    reportParametersSelector,
} from "@store/Framework/Store/Bunzl/Pages/Reporting/ReportOrderFrequency/ReportOrderFrequencySelectors";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import useInfinteScroll from "@store/Framework/Common/Hooks/useInfiniteScroll";
import useUpdateSearchFields from "@store/Framework/Store/Bunzl/Pages/Reporting/StoreHooks/useUpdateSearchFields";
import ReportingFilterGridItem from "@store/Library/Components/Reporting/Common/ReportingFilterGridItem";
import MonthlyExpenseTable from "@store/Library/Components/Reporting/MonthlyExpenseTable.tsx/MonthlyExpenseTable";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { useState } from "react";
import { useSelector } from "react-redux";

const DatePicker = getWidgetComponent("Reports/ReportSearchFieldDateRange");
const MemoizedDatePicker = React.memo(DatePicker);

export interface NamedMonthlyExpenseComparisonItemModel extends MonthlyExpenseComparisonItemModel {
    shipToName: string;
    shipToNumber: string;
}
export interface NamedExpenseModel {
    customerName: string;
    items: NamedMonthlyExpenseComparisonItemModel[];
}
const gridWidths: GridWidths[] = [12, 6, 12, 6, 6];

const MonthlyExpenseSearchForm: React.FC = () => {
    const onClear = useClear();
    const hasSetInitialValues = React.useRef(false);
    const [startInfiniteScroll, setStartInfiniteScroll] = useState(false);
    const { itemsToShow, setPage } = useInfinteScroll(1, startInfiniteScroll);
    const [sharedSearchTerm, setSharedSearchTerm] = useState("");
    const parameters = useSelector(reportParametersSelector);
    const updateSearchFields = useUpdateSearchFields();
    const [allReturnData, setAllReturnData] = useState<NamedExpenseModel>();
    // fixing a visual bug where the loading state would stutter using the hook isLoading
    const [isLoading, setIsLoading] = useState(false);
    const [individualCompanyDatas, setIndividualCompanyDatas] = React.useState<NamedExpenseModel[]>([]);
    const { updateData, isError, hasSubmitted } = useReportApi("monthlyExpenseComparison");
    const [areLocationsTogether, setAreLocationsTogether] = useState(true);
    const locationData = useSelector(locationsSelector);

    const handleClear = () => {
        onClear();
        setPage(1);
        setStartInfiniteScroll(false);
        setAllReturnData(undefined);
        setIndividualCompanyDatas([]);
    };

    const customerNameById = (id: string, locations: CompanyLocation[]) => {
        const location = locations.find(location => location.customers.find(customer => customer.id === id));
        const customer = location?.customers.find(customer => customer.id === id);
        return `${customer?.name ?? ""} (${customer?.customerSequence ?? ""})`;
    };

    const setInitalValues = () => {
        if (!hasSetInitialValues.current) {
            hasSetInitialValues.current = true;
            const sixMonthsAgo = new Date();
            sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
            updateSearchFields({
                ...parameters,
                startDate: sixMonthsAgo.toISOString(),
            });
        }
    };

    setInitalValues();

    const handleDataUpdate = async () => {
        setIsLoading(true);
        setAllReturnData(undefined);
        setIndividualCompanyDatas([]);
        const selectedCustomerIds = parameters.customers;

        const fullReturnPromise = updateData({
            ...parameters,
            reportName: "monthlyExpenseComparison",
            customers: selectedCustomerIds,
            ItemNumbers: [""],
        });

        const fullReturn = await fullReturnPromise;

        if (isMonthlyEpensiveComparisonModel(fullReturn)) {
            const nonEmptyFullReturn = fullReturn.customers.filter(customer => customer.items.length > 0);
            const individualCompanyDatas = nonEmptyFullReturn.map(customer => {
                const customerName = customerNameById(customer.customerId, locationData || []);
                const [shipToName, shipToNumber] = customerName.split(" (");
                const fixedReturn = customer.items.map(item => ({
                    ...item,
                    shipToName,
                    shipToNumber,
                    name: item.name.replace(/\(.*\)/, "").trim(),
                }));
                return {
                    items: fixedReturn,
                    customerName,
                };
            });
            setIndividualCompanyDatas(individualCompanyDatas);
            const allReturnData = individualCompanyDatas.reduce(
                (acc, company) => {
                    const items = [...acc.items, ...company.items];
                    return {
                        items,
                        customerName: translate("All"),
                    };
                },
                { items: [] as NamedMonthlyExpenseComparisonItemModel[], customerName: "" },
            );
            setAllReturnData(allReturnData);

            setStartInfiniteScroll(true);
            setIsLoading(false);
        }
    };
    if (!areLocationsTogether && itemsToShow > individualCompanyDatas.length && startInfiniteScroll) {
        setStartInfiniteScroll(false);
    }
    const handleLocationShowChange = () => {
        setAreLocationsTogether(!areLocationsTogether);
        setPage(1);
    };
    const memoizedTables = React.useMemo(() => {
        return areLocationsTogether
            ? allReturnData && (
                  <MonthlyExpenseTable
                      excelDataKeys={[]}
                      excelHeaders={[]}
                      sharedSearchTerm={sharedSearchTerm}
                      setSharedSearchTerm={setSharedSearchTerm}
                      monthlyExpenseData={allReturnData.items}
                      title={allReturnData.customerName}
                  />
              )
            : individualCompanyDatas.map(
                  (data, index) =>
                      itemsToShow > index && (
                          <MonthlyExpenseTable
                              excelDataKeys={["shipToName", "shipToNumber"]}
                              excelHeaders={["Ship To Name", "Ship To Number"]}
                              sharedSearchTerm={sharedSearchTerm}
                              setSharedSearchTerm={setSharedSearchTerm}
                              key={data.customerName + data.items[0].shipToName}
                              monthlyExpenseData={data.items}
                              title={data.customerName}
                          />
                      ),
              );
    }, [
        allReturnData,
        individualCompanyDatas,
        itemsToShow,
        sharedSearchTerm,
        setSharedSearchTerm,
        areLocationsTogether,
    ]);
    const isDataToDisplay = areLocationsTogether ? !!allReturnData : individualCompanyDatas.length > 0;
    const showSpinner = isLoading && hasSubmitted && !isDataToDisplay;
    return (
        <ReportContainer>
            <AccordionLayout>
                <ReportingFilterGridItem>
                    <MemoizedDatePicker />
                </ReportingFilterGridItem>

                <GridItem width={gridWidths}>
                    <LocationPicker isError={isError} />
                </GridItem>
                <ApiButtons isLoading={showSpinner} onClear={handleClear} updateData={handleDataUpdate} />
                <GridItem
                    css={css`
                        max-width: 342px !important;
                        padding-bottom: 1.5rem;
                    `}
                    width={[12, 6, 12, 8, 8]}
                >
                    <Checkbox checked={areLocationsTogether} onChange={handleLocationShowChange}>
                        {translate("Add locations together")}
                    </Checkbox>
                </GridItem>
            </AccordionLayout>
            <GridContainer
                css={css`
                    margin-top: 2rem;
                `}
            >
                <StyledWrapper
                    css={css`
                        padding: 0.5rem 1rem;
                        flex-direction: column;
                        width: 100%;
                    `}
                >
                    {memoizedTables}
                </StyledWrapper>
            </GridContainer>
        </ReportContainer>
    );
};
const widgetModule: CustomWidgetModule = {
    component: MonthlyExpenseSearchForm,
    definition: {
        group: "Reporting",
        displayName: "Monthly Expense Report",
        // allowedContexts: [MonthlyExpensePageContext],
    },
};

export default widgetModule;
